import React, {
  useEffect,
} from 'react';
import clsx from 'clsx';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import ToolTip from 'core/components/Tooltip';
import CollapseIcon from 'core/icons/collapse';
import Spinner from 'pages/Dashboard/components/Spinner';
import useSignalREffect from 'views/EMR/hooks/signalR';
import {
  useClientId,
} from 'views/EMR/stores/user';
import {
  useStickyActions,
  useStickyNotesLoading,
} from 'views/EMR/Sticky/stores/stickyNotesStore';
import {
  useStickyApi,
} from 'views/EMR/Sticky/hooks';
import StickyList from 'views/EMR/Sticky/components/StickyList';
import {
  ExtensionProps,
} from 'pages/Dashboard/contexts/viewContext';
import {
  StickyNotificationDTO,
} from 'dtos';
import {
  isNilOrZero,
} from '@populateui/utils';
import {
  Button,
} from 'pages/Dashboard/components/Button';

type HeaderProps = {
  disabled: boolean;
  onAdd: () => void;
  onClose?: () => void;
};

function Header({
  disabled,
  onAdd,
  onClose,
}: HeaderProps) {
  return (
    <div className="flex justify-between items-center !px-4 h-[40px]">
      <div className="flex items-center gap-2">
        {onClose && (
          <ToolTip title="Hide Stickies">
            <IconButton onClick={onClose}>
              <CollapseIcon className="!w-4 !h-4 !text-inherit" />
            </IconButton>
          </ToolTip>
        )}
        <h4 className="font-semibold text-sm">
          Stickies
        </h4>
      </div>
      <Button
        className="!font-semibold !py-1"
        variant="text"
        color="primary"
        type="button"
        onClick={onAdd}
        disabled={disabled}
        startIcon={<AddIcon className="!w-4 !h-4 !text-inherit" />}
      >
        Create Sticky
      </Button>
    </div>
  );
}

export default function StickyNotesPage({
  patient,
  className,
  classes,
  loaderSlot: _loaderSlot,
  onClose,
}: ExtensionProps) {
  const api = useStickyApi();
  const clientId = useClientId();
  const actions = useStickyActions();
  const isLoading = useStickyNotesLoading();
  const patientId = patient?.patientId ?? 0;

  // Inject API into store
  useEffect(() => {
    actions.setApi(api);
  }, [api, actions]);

  // Inject clientId into store
  useEffect(() => {
    if (clientId) {
      actions.setClientId(clientId);
    }
  }, [clientId, actions]);

  // Set active patient (loads stickies)
  useEffect(() => {
    if (patientId > 0) {
      actions.setActivePatient(patientId);
    }
  }, [patientId, actions]);

  // SignalR integration
  useSignalREffect(
    'OnStickyUpdated',
    async (data: StickyNotificationDTO) => {
      await actions.applySignalRNotification(data);
    },
    [actions],
  );

  const loaderSlot = _loaderSlot ?? (
    <div className="flex flex-auto justify-center gap-2 items-center h-full">
      <Spinner className="!font-bold" size={30} />
      Loading stickies...
    </div>
  );

  return (
    <div className={clsx('flex flex-col h-full', className)}>
      <Header
        disabled={isLoading || isNilOrZero(patientId)}
        onAdd={() => {
          actions.createSticky();
        }}
        onClose={onClose}
      />
      {(isLoading || isNilOrZero(patientId)) ? loaderSlot : (
        <StickyList
          classes={{
            list: clsx('gap-2 flex flex-col overflow-auto !h-full', classes?.list),
            empty: classes?.empty,
          }}
        />
      )}
    </div>
  );
}
